import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { AxiosResponse } from "axios";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import {
  getAllDocuments,
  getOneDocument,
} from "../../services/NewVersionService";
import {
  getVirtualReproWarehouse,
  createVirtualReproWarehouse,
  updateVirtualReproWarehouse,
} from "../../services/VirtualWarehouseService";
import "./VirtualWarehousePage.scss";

export interface VirtualWarehousePageProps {}
type VWDoc = any;

export const VirtualWarehousePage = (p: VirtualWarehousePageProps) => {
  const {} = p;

  const { t } = useTranslation();
  const toast = useRef<any>(null);

  const [processing, setProcessing] = useState<boolean>(false);
  const [vwDoc, setVwDoc] = useState<VWDoc>({
    id: undefined,
    repromaterialProducts: [],
    repromaterialProductsJSON: "[]",
  });

  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [productCategoriesApi, setProductCategoriesApi] = useState<
    ProductCategory[]
  >([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  const [newProduct, setNewProduct] = useState<Product>({
    id: "",
    name: "",
    type: ProductType.RawMaterial,
    code: "",
    category: {} as ProductCategory,
    lot: "",
    amount: "",
    measuringUnit: "",
  });

  // umanji po prijemnici
  const [documentIncNumber, setDocumentIncNumber] = useState<string>("");
  const [repromaterialInputsApi, setRepromaterialInputsApi] = useState<any[]>(
    [],
  );

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const productsDataTable: Product[] = useMemo(() => {
    const list = (vwDoc?.repromaterialProducts ?? []) as any[];
    const out: Product[] = [];

    for (const it of list) {
      const p = productsApi.find((x) => x.id === it.id);

      out.push({
        id: it?.id ?? "",
        type: ProductType.RawMaterial,
        name: p?.name ?? it?.name ?? "",
        code: p?.code ?? it?.code ?? "",
        category: p?.category ?? it?.category ?? ({} as any),
        lot: it?.lot ?? "",
        amount: it?.amount ?? "",
        measuringUnit: p?.measuringUnit ?? it?.measuringUnit ?? "",
        note: it?.note ?? "",
      } as any);
    }

    return out;
  }, [vwDoc, productsApi]);

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const [cResp, pResp] = await Promise.all([
          getAllProductCategories(),
          getAllProducts(),
        ]);
        ensureSuccess(cResp);
        ensureSuccess(pResp);

        const cats: ProductCategory[] = cResp?.data?.result ?? [];
        const pr: Product[] = pResp?.data?.result ?? [];

        setProductCategoriesApi(cats);
        setProductsApi(pr);
        setFilteredProducts(
          pr.filter((x) => x.type === ProductType.RawMaterial),
        );

        const year = new Date().getFullYear();
        const from = new Date(year, 0, 1, 0, 0, 1);
        const to = new Date(year, 11, 31, 23, 59, 59);

        const docsResp: AxiosResponse = await getAllDocuments(
          false, // repromaterijal
          true, // input
          from,
          to,
          false,
          undefined,
        );
        ensureSuccess(docsResp);
        setRepromaterialInputsApi(docsResp?.data?.result ?? []);

        // 1) GET vw doc
        const vwResp = await getVirtualReproWarehouse();
        ensureSuccess(vwResp);

        let doc = vwResp?.data?.result;

        // 2) ako ga nema -> POST init
        if (!doc?.id) {
          const initPayload = {
            repromaterialProductsJSON: "[]",
          };
          const created = await createVirtualReproWarehouse(initPayload);
          ensureSuccess(created);
          doc = created?.data?.result;
        }

        // parse JSON -> repromaterialProducts
        const json = doc?.repromaterialProductsJSON ?? "[]";
        let items: any[] = [];
        try {
          items = JSON.parse(json);
        } catch {
          items = [];
        }

        setVwDoc({
          ...doc,
          repromaterialProductsJSON: json,
          repromaterialProducts: items,
        });
      } catch (e: any) {
        console.error(e);
        toast.current?.show({
          severity: "error",
          detail:
            e?.response?.data?.message ??
            e?.message ??
            t("Greška pri učitavanju podataka"),
          life: 5000,
        });
      } finally {
        setProcessing(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const catId = newProduct.category?.id;
    if (!catId) {
      setFilteredProducts(
        productsApi.filter((x) => x.type === ProductType.RawMaterial),
      );
      return;
    }
    setFilteredProducts(productsApi.filter((x) => x.category?.id == catId));
  }, [newProduct, productsApi]);

  const handleNewProduct = (prop: string, value: any) => {
    setNewProduct((prev) => ({ ...prev, [prop]: value }));
  };

  const handleCategoryChange = (_prop: string, value: any) => {
    const cat = productCategoriesApi.find((c) => c.id === value);
    setNewProduct((prev) => ({ ...prev, category: cat as any }));
  };

  const handleProductChange = (_prop: string, value: any) => {
    const p = filteredProducts.find((x) => x.id === value);
    setNewProduct((prev) => ({
      ...prev,
      id: value,
      name: p?.name ?? "",
      measuringUnit: p?.measuringUnit ?? "",
      category: prev.category?.id ? prev.category : (p?.category as any),
      code: p?.code ?? "",
    }));
  };

  const addNewProduct = () => {
    if (!newProduct.id || newProduct.amount === "") {
      toast.current?.show({
        severity: "error",
        detail: t("Moraju biti popunjena polja za naziv i količinu proizvoda"),
        life: 3000,
      });
      return;
    }

    const copy = cloneDeep(vwDoc);
    const list: any[] = copy.repromaterialProducts ?? [];

    const idx = list.findIndex((x) => x.id == newProduct.id);
    const payloadItem = cloneDeep(newProduct);

    if (idx >= 0) list[idx] = payloadItem;
    else list.push(payloadItem);

    copy.repromaterialProducts = list;
    copy.repromaterialProductsJSON = JSON.stringify(list);

    setVwDoc(copy);

    setNewProduct({
      id: "",
      name: "",
      type: ProductType.RawMaterial,
      code: "",
      category: {} as ProductCategory,
      lot: "",
      amount: "",
      measuringUnit: "",
    });
  };

  const deleteActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-trash"
        onButtonClick={() => {
          const copy = cloneDeep(vwDoc);
          copy.repromaterialProducts = (
            copy.repromaterialProducts ?? []
          ).filter((x: any) => x.id != rowData.id);
          copy.repromaterialProductsJSON = JSON.stringify(
            copy.repromaterialProducts ?? [],
          );
          setVwDoc(copy);

          setNewProduct({
            id: "",
            name: "",
            type: ProductType.RawMaterial,
            code: "",
            category: {} as ProductCategory,
            lot: "",
            amount: "",
            measuringUnit: "",
            note: "",
          } as any);
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  const editActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        labelText=""
        icon="bi bi-pencil"
        onButtonClick={() => {
          setNewProduct(rowData);
        }}
        addStyle={{
          backgroundColor: "white",
          color: "rgba(21, 115, 71, 1)",
          border: "none",
        }}
      />
    );
  };

  const nameOfProduct = (rowData: any) => rowData.name;
  const nameOfCategory = (rowData: any) => rowData.category?.name ?? "";

  const onSubmit = async () => {
    if (processing) return;
    setProcessing(true);

    try {
      const copy = cloneDeep(vwDoc);

      copy.repromaterialProductsJSON = JSON.stringify(
        copy.repromaterialProducts ?? [],
      );

      let resp;
      if (copy?.id) {
        resp = await updateVirtualReproWarehouse({
          repromaterialProductsJSON: copy.repromaterialProductsJSON,
        });
      } else {
        resp = await createVirtualReproWarehouse({
          repromaterialProductsJSON: copy.repromaterialProductsJSON,
        });
      }

      ensureSuccess(resp);

      const saved = resp?.data?.result;
      const json =
        saved?.repromaterialProductsJSON ?? copy.repromaterialProductsJSON;

      let items: any[] = [];
      try {
        items = JSON.parse(json);
      } catch {
        items = [];
      }

      setVwDoc({
        ...saved,
        repromaterialProductsJSON: json,
        repromaterialProducts: items,
      });

      toast.current?.show({
        severity: "success",
        detail: t("Uspešno sačuvano stanje virtuelnog magacina"),
        life: 2500,
      });
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          t("Greška pri snimanju virtuelnog magacina"),
        life: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleDocumentIncId = async () => {
    if (processing) return;

    const input = (documentIncNumber ?? "").trim();
    if (!input) {
      toast.current?.show({
        severity: "error",
        detail: t("Nije unet broj prijemnice"),
        life: 3000,
      });
      return;
    }

    const docShort = repromaterialInputsApi.find((d) => {
      const byNumber = (d.finishedDocumentNumber ?? "").toString() === input;
      const byId = (d.finishedDocumentId ?? "").toString() === input;
      return byNumber || byId;
    });

    if (!docShort?.id) {
      toast.current?.show({
        severity: "error",
        detail: t("Nije pronađena prijemnica sa unetim brojem"),
        life: 3000,
      });
      return;
    }

    setProcessing(true);
    try {
      const fullResp: AxiosResponse = await getOneDocument(docShort.id);
      ensureSuccess(fullResp);

      const fullDoc = fullResp?.data?.result as any;
      const json = fullDoc?.repromaterialProductsJSON ?? "[]";

      let productsFromDoc: any[] = [];
      try {
        productsFromDoc = JSON.parse(json);
      } catch {
        productsFromDoc = [];
      }

      const copy = cloneDeep(vwDoc);
      const list: any[] = copy.repromaterialProducts ?? [];

      for (const oneProduct of productsFromDoc) {
        const idx = list.findIndex((p: any) => p.id == oneProduct.id);
        if (idx >= 0) {
          const current = parseFloat(list[idx].amount ?? "0");
          const dec = parseFloat(oneProduct.amount ?? "0");
          const next = current - dec;
          list[idx].amount = (next > 0 ? next : 0).toString();
        }
      }

      copy.repromaterialProducts = list;
      copy.repromaterialProductsJSON = JSON.stringify(list);
      setVwDoc(copy);

      toast.current?.show({
        severity: "success",
        detail: t("Stanje virtuelnog magacina je umanjeno (klikni Sačuvaj)"),
        life: 3000,
      });
    } catch (e: any) {
      console.error(e);
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          t("Greška pri umanjenju virtuelnog magacina"),
        life: 5000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({ mode: "all" });

  const addButtonSaveStyle: React.CSSProperties = {
    background: "rgba(0, 165, 83, 1)",
    border: "none",
    margin: "0 .5rem",
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-left">
            <div className="header-title">
              <i
                className="bi bi-shop-window header-title-icon"
                style={{ fontSize: 18 }}
              />
              <span className="header-title-left">
                {t("Roba u virtuelnom magacinu")}
              </span>
            </div>
          </div>
          <div className="header-right">
            <InputText
              id="documentIdInput"
              value={documentIncNumber}
              onChange={(e) => setDocumentIncNumber(e.target.value)}
              placeholder={"Unesite broj prijemnice"}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 1rem 0 2rem",
              }}
              labelText={t("Umanji")}
              icon="bi bi-pencil"
              onButtonClick={handleDocumentIncId}
              disabled={processing}
            />
          </div>
        </div>
        <div className="form-body">
          <DropdownInput
            id="category"
            value={newProduct.category?.id}
            onValueChange={handleCategoryChange}
            optionValue="id"
            optionLabel="name"
            options={productCategoriesApi.filter(
              (x: any) => x.type == ProductType.RawMaterial,
            )}
            filter={false}
            labelText={t("Tip proizvoda *")}
            placeholderText={t("Izaberite tip proizvoda")}
            control={control}
            setValue={setValue}
            errors={errors}
            disabled={processing}
          />
          <DropdownInput
            id="name"
            value={newProduct?.id}
            onValueChange={handleProductChange}
            optionValue="id"
            optionLabel="name"
            options={filteredProducts}
            filter={true}
            labelText={t("Naziv proizvoda *")}
            placeholderText={t("Izaberite proizvod")}
            control={control}
            setValue={setValue}
            errors={errors}
            disabled={processing}
          />
          <TextInputWithIcon
            id="amount"
            value={newProduct.amount as string}
            onValueChange={handleNewProduct}
            labelText={t("Količina *")}
            icon="bi bi-minecart-loaded"
            iconAlign="right"
            placeholderText={t("Unesite količinu")}
            control={control}
            setValue={setValue}
            errors={errors}
            rules={{
              pattern: {
                value: /^\d+\.?\d*$/,
                message: "Please enter a number",
              },
            }}
            disabled={processing}
          />
          <div className="add-product-item">
            <ButtonInput
              labelText={t("Dodaj proizvod")}
              icon="bi bi-plus-square"
              onButtonClick={addNewProduct}
              addStyle={{
                border: "none",
                background: "rgba(21, 115, 71, 1)",
                color: "#ffffff",
                height: "2.6rem",
              }}
              disabled={processing}
            />
          </div>
          <div className="data-table-container">
            <DataTable value={productsDataTable}>
              <Column body={nameOfProduct} header={t("Naziv proizvoda:")} />
              <Column body={nameOfCategory} header={t("Tip proizvoda:")} />
              <Column field="amount" header={t("Količina:")} />
              <Column field="measuringUnit" header={t("Merna jed:")} />
              <Column
                body={editActionBodyTemplate}
                className="datatable-button-column"
                header={`${t("Izmeni")}:`}
              />
              <Column
                body={deleteActionBodyTemplate}
                className="datatable-button-column"
                header={`${t("Izbriši")}:`}
              />
            </DataTable>
          </div>

          <div className="divider-line">
            <Divider />
          </div>
          <div></div>
          <div className="footer-buttons">
            <ButtonInput
              labelText={t("Sačuvaj")}
              onButtonClick={onSubmit}
              addStyle={addButtonSaveStyle}
              disabled={processing}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
